import React, { FC, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getUserFetch } from 'src/resources/user/reducer';
import { setLogged } from 'src/resources/login/reducer';
import { RootStoreType } from 'src/types/store';
import LoginContainer from '../LoginContainer';
import LayoutContainer from '../LayoutContainer';
import RoutesContainer from 'src/containers/RoutesContainer';
import ChangePasswordContainer from 'src/containers/ChangePasswordContainer';
import Loading from 'src/components/Loading/intex';
import AlertMessagesContainer from '../AlertMessagesContainer/intex';
import ResetPasswordContainer from '../ResetPasswordContainer';
import { useLocation } from 'react-router-dom';
import PasswordChangedDialog from 'src/containers/PasswordChangedDialog';
import { bootstrapDebug } from 'src/utils/bootstrapDebug';

type AppViewType =
    | 'initial'
    | 'resetPassword'
    | 'userLoading'
    | 'changePassword'
    | 'app'
    | 'login'
    | 'loggedWithoutUser';

const AppContainer: FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const lastLoggedViewRef = useRef<AppViewType | null>(null);

    const logged = useSelector((state: RootStoreType) => state.login.logged);
    const userLoading = useSelector((state: RootStoreType) => state.user.loading);
    const userData = useSelector((state: RootStoreType) => state.user.data);
    const userError = useSelector((state: RootStoreType) => state.user.error);
    const passwordChangedRelogin = useSelector((state: RootStoreType) => state.user.passwordChangedRelogin);

    useEffect(() => {
        const hasToken = Boolean(localStorage.getItem('token'));
        bootstrapDebug('AppContainer:mount', { hasToken });
        if (hasToken) {
            dispatch(getUserFetch());
        } else {
            dispatch(setLogged(false));
        }
    }, []);

    const changePasswordNeed = userData && userData.TIMESTAMP_CREATED === userData.LAST_CHANGED_PASSWORD;

    let view: AppViewType = 'initial';
    let renderComponent = (
        <div>
            <Loading />
        </div>
    );
    if (location.pathname.split('/')[1] === 'public' && location.pathname.split('/')[2] === 'resetPassword') {
        view = 'resetPassword';
        renderComponent = <ResetPasswordContainer />;
    } else if (userLoading) {
        view = 'userLoading';
        renderComponent = (
            <div>
                <Loading />
            </div>
        );
    } else if (changePasswordNeed) {
        view = 'changePassword';
        renderComponent = (
            <LayoutContainer>
                <ChangePasswordContainer required />
            </LayoutContainer>
        );
    } else if (userData) {
        view = 'app';
        renderComponent = (
            <>
                <LayoutContainer>
                    <RoutesContainer />
                </LayoutContainer>
                <AlertMessagesContainer />
            </>
        );
    } else if (!logged) {
        view = 'login';
        renderComponent = <LoginContainer />;
    } else {
        view = 'loggedWithoutUser';
        renderComponent = (
            <div>
                <Loading />
            </div>
        );
    }

    useEffect(() => {
        if (lastLoggedViewRef.current === view) {
            return;
        }
        lastLoggedViewRef.current = view;
        bootstrapDebug('AppContainer:view', {
            view,
            logged,
            userLoading,
            hasUserData: Boolean(userData),
            userError,
            pathname: location.pathname,
        });
    }, [view, logged, userLoading, userData, userError, location.pathname]);

    return (
        <div className="App">
            {renderComponent}
            {passwordChangedRelogin && <PasswordChangedDialog />}
        </div>
    );
};

export default AppContainer;
